import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getCheckoutSession, confirmCheckout, cancelCheckout } from '../api/payments';
import { trackPixelEvent } from '../hooks/useMetaPixel';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Lock, CreditCard, ShieldCheck, ArrowLeft, CheckCircle2, Sparkles } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { fmtCredits, creditsToEur } from '../lib/currency';

const Field = ({ label, ...props }) => (
  <div>
    <label className="mb-1 block text-xs font-semibold text-foreground">{label}</label>
    <Input {...props} className={`h-10 ${props.className || ''}`} />
  </div>
);

const CheckoutPage = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [done, setDone] = useState(false);

  // Mock card fields
  const [card, setCard] = useState({
    name: '',
    number: '4242 4242 4242 4242',
    expiry: '12 / 34',
    cvc: '123',
    zip: '00100',
  });

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const data = await getCheckoutSession(sessionId);
        if (alive) setSession(data);
      } catch (err) {
        toast({
          title: 'Checkout error',
          description: err.response?.data?.detail || 'Could not load session.',
          variant: 'destructive',
        });
        navigate('/');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const handlePay = async () => {
    setPaying(true);
    try {
      // simulate network latency for realism
      await new Promise((r) => setTimeout(r, 900));
      await confirmCheckout(sessionId);
      const isTopup = session?.item_type === 'wallet_topup';
      const isLive = session?.item_type === 'live';
      // Phase 37E — event_id ensures Meta dedup with the server-side
      // Purchase fired by payments.py (event_id=`purchase_{session_id}`).
      // For wallet_topup keep content_type explicit so this Purchase
      // stays REPORTABLE but SEPARATE from real content purchases in
      // Meta Event Manager.
      trackPixelEvent(
        'Purchase',
        {
          value: creditsToEur(session?.amount || 0),
          currency: 'EUR',
          content_ids: isTopup
            ? [`topup_${sessionId}`]
            : [session?.item_id || session?.post_id].filter(Boolean),
          content_type: isTopup ? 'wallet_topup' : (session?.item_type || 'post'),
        },
        isTopup ? `topup_${sessionId}` : `purchase_${sessionId}`,
      );
      setDone(true);
      toast({
        title: isTopup
          ? `Wallet ricaricato — ${fmtCredits(session?.amount)}`
          : isLive
            ? 'Prenotazione confermata'
            : 'Pagamento riuscito',
        description: isTopup
          ? 'Your new balance is ready to use.'
          : isLive
            ? 'Hai prenotato la live. Riceverai un promemoria poco prima dell\u2019inizio.'
            : 'The post is now unlocked.',
      });
      setTimeout(() => {
        if (isTopup) navigate('/shop');
        else if (isLive) navigate(`/lives/${session?.item_id || session?.post_id}`);
        else navigate('/');
      }, 1400);
    } catch (err) {
      toast({
        title: 'Payment failed',
        description: err.response?.data?.detail || 'Please try again.',
        variant: 'destructive',
      });
    } finally {
      setPaying(false);
    }
  };

  const handleCancel = async () => {
    try {
      await cancelCheckout(sessionId);
    } catch (e) {
      /* ignore */
    }
    navigate(-1);
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-border border-t-neutral-800" />
      </div>
    );
  }

  if (!session) return null;

  if (done || session.status === 'completed') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-4">
        <div className="flex max-w-md flex-col items-center rounded-2xl bg-card p-10 text-center shadow-sm">
          <CheckCircle2 className="h-14 w-14 text-emerald-500" strokeWidth={1.6} />
          <h1 className="mt-4 text-xl font-semibold">Pagamento riuscito</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Acquisto di {fmtCredits(session.amount)} completato. Il contenuto è ora disponibile.
          </p>
          <Button
            onClick={() => navigate('/')}
            className="mt-6 h-10 gold-gradient px-6"
          >
            Torna al feed
          </Button>
        </div>
      </div>
    );
  }

  const preview = session.post_preview;

  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto max-w-3xl px-4 py-6 md:py-12">
        <button
          onClick={handleCancel}
          className="mb-4 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" /> Back
        </button>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-[1fr_360px]">
          {/* Payment form */}
          <div className="rounded-2xl bg-card p-6 shadow-sm">
            <div className="mb-4 flex items-center gap-2 text-sm font-semibold text-foreground">
              <span className="inline-flex h-7 items-center rounded-md bg-[#635BFF] px-2 text-xs font-bold text-white">
                stripe
              </span>
              <span className="text-muted-foreground/70">test mode</span>
            </div>
            <h2 className="text-xl font-semibold">Pay with card</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              This is a simulated checkout. No real charge will be made.
            </p>

            <div className="mt-5 grid grid-cols-1 gap-3">
              <Field
                label="Email"
                placeholder="you@example.com"
                value={card.name}
                onChange={(e) => setCard({ ...card, name: e.target.value })}
              />
              <div>
                <label className="mb-1 block text-xs font-semibold text-foreground">
                  Card information
                </label>
                <div className="flex flex-col rounded-md border border-border focus-within:ring-2 focus-within:ring-[#635BFF]/30">
                  <Input
                    value={card.number}
                    onChange={(e) => setCard({ ...card, number: e.target.value })}
                    placeholder="1234 1234 1234 1234"
                    className="h-10 rounded-none border-0 border-b focus-visible:ring-0"
                  />
                  <div className="grid grid-cols-2">
                    <Input
                      value={card.expiry}
                      onChange={(e) => setCard({ ...card, expiry: e.target.value })}
                      placeholder="MM / YY"
                      className="h-10 rounded-none border-0 border-r focus-visible:ring-0"
                    />
                    <Input
                      value={card.cvc}
                      onChange={(e) => setCard({ ...card, cvc: e.target.value })}
                      placeholder="CVC"
                      className="h-10 rounded-none border-0 focus-visible:ring-0"
                    />
                  </div>
                </div>
              </div>
              <Field
                label="Country / ZIP"
                value={card.zip}
                onChange={(e) => setCard({ ...card, zip: e.target.value })}
              />
            </div>

            <Button
              onClick={handlePay}
              disabled={paying}
              className="mt-6 h-11 w-full bg-[#635BFF] text-base font-semibold hover:bg-[#5247E6] disabled:opacity-70"
            >
              {paying ? (
                <span className="flex items-center gap-2">
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                  Processing...
                </span>
              ) : (
                <>
                  <Lock className="mr-2 h-4 w-4" />
                  {session?.item_type === 'wallet_topup'
                    ? `Paga €${creditsToEur(session.amount).toFixed(2)} per ${fmtCredits(session.amount)}`
                    : `Paga ${fmtCredits(session.amount)} dal Wallet`}
                </>
              )}
            </Button>
            <p className="mt-3 flex items-center justify-center gap-1 text-[11px] text-muted-foreground">
              <ShieldCheck className="h-3 w-3" /> Payments are secure and encrypted (mock).
            </p>
          </div>

          {/* Order summary */}
          <aside className="rounded-2xl bg-card p-6 shadow-sm">
            <p className="text-xs uppercase tracking-wider text-muted-foreground">Order summary</p>
            <div className="mt-3 flex items-center gap-3">
              {preview?.creator_avatar ? (
                <img
                  src={preview.creator_avatar}
                  alt={preview?.creator_username}
                  className="h-12 w-12 rounded-full object-cover"
                />
              ) : (
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted text-base font-semibold text-muted-foreground">
                  {(preview?.creator_username || '?').charAt(0).toUpperCase()}
                </div>
              )}
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold">@{preview?.creator_username}</p>
                <p className="text-xs text-muted-foreground">{preview?.creator_full_name}</p>
              </div>
            </div>
            <div className="mt-4 rounded-lg border border-border p-3">
              <p className="flex items-center gap-1 text-xs font-semibold text-muted-foreground">
                <Sparkles className="h-3 w-3" /> {session?.item_type === 'live'
                  ? 'Biglietto live'
                  : session?.item_type === 'wallet_topup'
                    ? 'Ricarica wallet'
                    : `Unlock ${preview?.media_type || ''} post`}
              </p>
              {preview?.caption && (
                <p className="mt-1 line-clamp-3 text-sm text-foreground">{preview.caption}</p>
              )}
            </div>
            <div className="mt-4 space-y-2 text-sm">
              <div className="flex justify-between text-muted-foreground">
                <span>Subtotale</span>
                <span>{fmtCredits(session.amount)}</span>
              </div>
              <div className="flex justify-between text-muted-foreground">
                <span>Tasse</span>
                <span>{fmtCredits(0)}</span>
              </div>
              <div className="flex justify-between border-t border-border pt-2 text-base font-semibold">
                <span>Totale</span>
                <span>{fmtCredits(session.amount)} <span className="text-xs text-muted-foreground">(≈ €{creditsToEur(session.amount).toFixed(2)})</span></span>
              </div>
            </div>
            <p className="mt-4 text-[11px] text-muted-foreground">
              By completing this purchase, you agree to our Terms. One-time payment, instant access.
            </p>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
